'use client';

import { useState, useEffect } from 'react';
import PhaseCard from './PhaseCard';
import ImpactMetricCard from './ImpactMetricCard';
import SectorFocusCard from './SectorFocusCard';
import DownloadResourceCard from './DownloadResourceCard';
import TimelineVisualization from './TimelineVisualization';
import NewsletterSubscription from './NewsletterSubscription';

interface Phase {
  number: number;
  title: string;
  duration: string;
  description: string;
  keyObjectives: string[];
  milestones: Array<{
    title: string;
    description: string;
    timeline: string;
  }>;
  investmentRange: string;
  expectedImpact: string;
}

interface ImpactMetric {
  icon: string;
  title: string;
  baseline: string;
  year5Target: string;
  year10Target: string;
  year20Target: string;
  description: string;
}

interface SectorFocus {
  icon: string;
  title: string;
  description: string;
  investmentThesis: string;
  keyInitiatives: string[];
  targetAllocation: string;
}

interface DownloadResource {
  title: string;
  description: string;
  fileSize: string;
  fileType: string;
  icon: string;
  fileUrl: string;
}

interface TimelineEvent {
  year: number;
  quarter: string;
  title: string;
  description: string;
  phase: number;
  status: 'completed' | 'in-progress' | 'planned';
}

export default function TransformationAgendaInteractive() {

  const phases: Phase[] = [
    {
      number: 1,
      title: "Foundation & Proof of Concept",
      duration: "Years 1-5 (2026-2030)",
      description: "Establish operational infrastructure, validate investment thesis through pilot projects, and build institutional credibility with measurable early wins in Kenya's priority sectors.",
      keyObjectives: [
        "Deploy $50-100M in 3-5 anchor investments across manufacturing, agriculture, and infrastructure",
        "Establish Nairobi headquarters with full operational capabilities and regulatory compliance",
        "Build strategic partnerships with Kenyan government, development finance institutions, and local stakeholders",
        "Achieve 15-20% IRR on initial portfolio while creating 5,000+ direct jobs",
        "Develop proprietary impact measurement framework and quarterly reporting systems"
      ],
      milestones: [
        {
          title: "Operational Launch",
          description: "Complete regulatory approvals, establish headquarters, and hire core team of 15-20 professionals",
          timeline: "Q1-Q2 2026"
        },
        {
          title: "First Anchor Investment",
          description: "Close $20-30M manufacturing facility investment with government co-investment partnership",
          timeline: "Q3 2026"
        },
        {
          title: "Portfolio Diversification",
          description: "Deploy capital across 3 sectors with 5 total investments and establish monitoring systems",
          timeline: "2027-2028"
        },
        {
          title: "Impact Validation",
          description: "Publish first comprehensive impact report with third-party verification of job creation and economic outcomes",
          timeline: "Q4 2028"
        }
      ],
      investmentRange: "$50-100M",
      expectedImpact: "5,000+ Jobs Created"
    },
    {
      number: 2,
      title: "Regional Expansion & Scale",
      duration: "Years 6-12 (2031-2037)",
      description: "Leverage proven Kenya model to expand across East Africa, scale investment capacity, and establish Grandville Holdings as the premier development finance partner for institutional transformation.",
      keyObjectives: [
        "Expand operations to Tanzania, Uganda, Rwanda, and Ethiopia with localized teams",
        "Increase annual deployment capacity to $200-300M across 15-20 active investments",
        "Establish sector-specific expertise centers for manufacturing, agriculture, infrastructure, and technology",
        "Achieve portfolio-wide 18-22% IRR while creating 25,000+ cumulative jobs",
        "Launch Grandville Development Finance Institute for capacity building and knowledge sharing"
      ],
      milestones: [
        {
          title: "Tanzania Market Entry",
          description: "Establish Dar es Salaam office and close first $30-40M infrastructure investment",
          timeline: "2031-2032"
        },
        {
          title: "Multi-Country Portfolio",
          description: "Active investments in 4+ countries with diversified sector exposure and local partnerships",
          timeline: "2033-2034"
        },
        {
          title: "Institutional Recognition",
          description: "Secure investment-grade credit rating and establish $500M+ fundraising capacity",
          timeline: "2035"
        },
        {
          title: "Knowledge Platform Launch",
          description: "Publish comprehensive development finance methodology and train 500+ regional professionals",
          timeline: "2036-2037"
        }
      ],
      investmentRange: "$200-300M/year",
      expectedImpact: "25,000+ Cumulative Jobs"
    },
    {
      number: 3,
      title: "Pan-African Leadership & Legacy",
      duration: "Years 13-20 (2038-2045)",
      description: "Establish Grandville Holdings as the definitive model for patient capital and sustainable development finance across Africa, with proven methodology, institutional permanence, and generational impact.",
      keyObjectives: [
        "Operate in 10+ African countries with $500M+ annual deployment capacity",
        "Achieve $5B+ cumulative investment portfolio with 20-25% IRR and 100,000+ jobs created",
        "Establish Grandville as preferred partner for sovereign wealth funds and multilateral institutions",
        "Launch successor fund structures ensuring institutional continuity beyond founding team",
        "Create replicable development finance model adopted by regional governments and institutions"
      ],
      milestones: [
        {
          title: "Pan-African Footprint",
          description: "Active operations in West, East, and Southern Africa with regional headquarters and specialized teams",
          timeline: "2038-2040"
        },
        {
          title: "Institutional Permanence",
          description: "Establish endowment structure, governance framework, and leadership succession ensuring 50+ year continuity",
          timeline: "2041-2042"
        },
        {
          title: "Methodology Replication",
          description: "License Grandville approach to 5+ regional development finance institutions and government agencies",
          timeline: "2043"
        },
        {
          title: "Legacy Achievement",
          description: "Reach 100,000+ jobs created, $5B+ deployed, and establish Grandville as permanent African institution",
          timeline: "2044-2045"
        }
      ],
      investmentRange: "$500M+/year",
      expectedImpact: "100,000+ Total Jobs"
    }
  ];

  const impactMetrics: ImpactMetric[] = [
    {
      icon: "BriefcaseIcon",
      title: "Direct Job Creation",
      baseline: "0 jobs",
      year5Target: "5,000 jobs",
      year10Target: "25,000 jobs",
      year20Target: "100,000 jobs",
      description: "Formal employment opportunities created through portfolio companies, measured quarterly with third-party verification and demographic tracking."
    },
    {
      icon: "CurrencyDollarIcon",
      title: "Capital Deployed",
      baseline: "$0",
      year5Target: "$100M",
      year10Target: "$1.5B",
      year20Target: "$5B+",
      description: "Cumulative investment capital deployed across sectors and geographies, demonstrating institutional scale and systematic deployment capability."
    },
    {
      icon: "ChartBarIcon",
      title: "Portfolio IRR",
      baseline: "N/A",
      year5Target: "15-20%",
      year10Target: "18-22%",
      year20Target: "20-25%",
      description: "Risk-adjusted returns demonstrating sustainable financial performance while achieving development impact, validated by independent auditors."
    },
    {
      icon: "BuildingOfficeIcon",
      title: "Portfolio Companies",
      baseline: "0 companies",
      year5Target: "5 companies",
      year10Target: "20 companies",
      year20Target: "50+ companies",
      description: "Active investments across sectors and stages, from early-stage ventures to established enterprises, each contributing to regional economic transformation."
    },
    {
      icon: "GlobeAltIcon",
      title: "Geographic Reach",
      baseline: "0 countries",
      year5Target: "1 country",
      year10Target: "5 countries",
      year20Target: "10+ countries",
      description: "Operational presence across African markets, demonstrating replicable methodology and institutional capacity for multi-country portfolio management."
    },
    {
      icon: "UserGroupIcon",
      title: "Indirect Beneficiaries",
      baseline: "0 people",
      year5Target: "25,000 people",
      year10Target: "150,000 people",
      year20Target: "500,000+ people",
      description: "Families and communities benefiting from job creation, supply chain development, and economic multiplier effects, measured through household surveys."
    }
  ];

  const sectorFocuses: SectorFocus[] = [
    {
      icon: "CogIcon",
      title: "Manufacturing & Industrial",
      description: "Value-added production facilities creating formal employment and building export capacity across consumer goods, textiles, and light manufacturing sectors.",
      investmentThesis: "Africa's demographic dividend requires manufacturing job creation at scale. We target facilities employing 500-2,000 workers with clear export potential and technology transfer opportunities.",
      keyInitiatives: [
        "Textile and garment manufacturing with regional export focus",
        "Consumer goods production serving growing African middle class",
        "Light manufacturing with technology transfer and skills development",
        "Industrial park development with shared infrastructure and services"
      ],
      targetAllocation: "35-40%"
    },
    {
      icon: "TruckIcon",
      title: "Agriculture & Agribusiness",
      description: "Commercial farming, processing, and distribution infrastructure transforming smallholder agriculture into formal value chains with sustainable practices.",
      investmentThesis: "Agriculture employs 60%+ of African workforce but lacks processing infrastructure. We invest in value-addition, cold chain, and market linkages creating formal jobs and farmer income.",
      keyInitiatives: [
        "Commercial farming operations with outgrower programs",
        "Processing facilities for fruits, vegetables, and grains",
        "Cold chain and logistics infrastructure development",
        "Agricultural technology and mechanization services"
      ],
      targetAllocation: "25-30%"
    },
    {
      icon: "BuildingOffice2Icon",
      title: "Infrastructure & Real Estate",
      description: "Economic infrastructure enabling business growth and job creation, including industrial facilities, logistics hubs, and affordable housing developments.",
      investmentThesis: "Infrastructure deficits constrain economic growth. We develop facilities that enable other businesses to scale while creating construction jobs and permanent employment.",
      keyInitiatives: [
        "Industrial park and special economic zone development",
        "Logistics and warehousing facilities for trade growth",
        "Affordable housing for emerging middle class",
        "Renewable energy and utility infrastructure"
      ],
      targetAllocation: "20-25%"
    },
    {
      icon: "ComputerDesktopIcon",
      title: "Technology & Services",
      description: "Digital infrastructure, business services, and technology-enabled solutions creating skilled employment and supporting broader economic transformation.",
      investmentThesis: "Technology enables efficiency and scale across sectors. We invest in platforms and services that create skilled jobs while supporting portfolio company growth and regional competitiveness.",
      keyInitiatives: [
        "Business process outsourcing and shared services centers",
        "Financial technology and digital payment infrastructure",
        "Education technology and skills training platforms",
        "Healthcare technology and service delivery innovation"
      ],
      targetAllocation: "15-20%"
    }
  ];

  const downloadResources: DownloadResource[] = [
    {
      title: "Complete Transformation Framework",
      description: "Comprehensive 120-page document detailing our three-phase implementation strategy, financial projections, risk management approach, and impact measurement methodology.",
      fileSize: "8.5 MB",
      fileType: "PDF",
      icon: "DocumentTextIcon",
      fileUrl: "/documents/transformation-framework.pdf"
    },
    {
      title: "Investment Thesis & Methodology",
      description: "Detailed analysis of our sector selection criteria, due diligence process, portfolio construction approach, and systematic investment methodology for institutional investors.",
      fileSize: "4.2 MB",
      fileType: "PDF",
      icon: "ChartBarIcon",
      fileUrl: "/documents/investment-thesis.pdf"
    },
    {
      title: "Impact Measurement Framework",
      description: "Proprietary framework for tracking job creation, economic impact, and sustainable development outcomes with quarterly reporting templates and verification protocols.",
      fileSize: "3.8 MB",
      fileType: "PDF",
      icon: "ClipboardDocumentCheckIcon",
      fileUrl: "/documents/impact-measurement-framework.pdf"
    },
    {
      title: "Q4 2025 Progress Report",
      description: "Latest quarterly update on portfolio performance, new investments, job creation metrics, and strategic developments across our operational footprint.",
      fileSize: "2.1 MB",
      fileType: "PDF",
      icon: "NewspaperIcon",
      fileUrl: "/documents/q4-2025-progress-report.pdf"
    }
  ];

  const timelineEvents: TimelineEvent[] = [
    {
      year: 2026,
      quarter: "Q1",
      title: "Regulatory Approval & Headquarters Establishment",
      description: "Complete all regulatory approvals with Kenyan authorities and establish Nairobi headquarters with core operational team.",
      phase: 1,
      status: "planned"
    },
    {
      year: 2026,
      quarter: "Q3",
      title: "First Anchor Investment Closed",
      description: "Deploy $25M in manufacturing facility with government co-investment partnership, creating 800+ direct jobs.",
      phase: 1,
      status: "planned"
    },
    {
      year: 2027,
      quarter: "Q2",
      title: "Agriculture Sector Entry",
      description: "Close $18M investment in commercial farming and processing operation with 1,200+ job creation target.",
      phase: 1,
      status: "planned"
    },
    {
      year: 2028,
      quarter: "Q1",
      title: "Infrastructure Investment Launch",
      description: "Deploy $30M in industrial park development with shared services and utilities infrastructure.",
      phase: 1,
      status: "planned"
    },
    {
      year: 2028,
      quarter: "Q4",
      title: "First Impact Report Published",
      description: "Release comprehensive impact assessment with third-party verification of 3,500+ jobs created and economic outcomes.",
      phase: 1,
      status: "planned"
    },
    {
      year: 2030,
      quarter: "Q4",
      title: "Phase 1 Completion & Validation",
      description: "Achieve 5,000+ jobs created, 15-20% portfolio IRR, and establish foundation for regional expansion.",
      phase: 1,
      status: "planned"
    },
    {
      year: 2031,
      quarter: "Q2",
      title: "Tanzania Market Entry",
      description: "Establish Dar es Salaam office and close first $35M infrastructure investment with local partnerships.",
      phase: 2,
      status: "planned"
    },
    {
      year: 2033,
      quarter: "Q1",
      title: "Multi-Country Portfolio Established",
      description: "Active investments in Kenya, Tanzania, Uganda, and Rwanda with diversified sector exposure.",
      phase: 2,
      status: "planned"
    },
    {
      year: 2035,
      quarter: "Q3",
      title: "Investment-Grade Rating Achieved",
      description: "Secure investment-grade credit rating from international agency, enabling $500M+ fundraising capacity.",
      phase: 2,
      status: "planned"
    },
    {
      year: 2037,
      quarter: "Q4",
      title: "Phase 2 Completion & Scale Validation",
      description: "Reach 25,000+ cumulative jobs, $1.5B deployed, and establish regional leadership position.",
      phase: 2,
      status: "planned"
    },
    {
      year: 2040,
      quarter: "Q2",
      title: "Pan-African Footprint Established",
      description: "Active operations in 10+ countries with regional headquarters and specialized sector teams.",
      phase: 3,
      status: "planned"
    },
    {
      year: 2042,
      quarter: "Q4",
      title: "Institutional Permanence Framework",
      description: "Establish endowment structure and governance ensuring 50+ year institutional continuity.",
      phase: 3,
      status: "planned"
    },
    {
      year: 2045,
      quarter: "Q4",
      title: "Legacy Achievement & Replication",
      description: "Reach 100,000+ jobs created, $5B+ deployed, and establish Grandville as permanent African institution.",
      phase: 3,
      status: "planned"
    }
  ];

  return (
    <div className="space-y-4 sm:space-y-8">
      <section>
        <h2 className="font-headline text-2xl font-semibold text-foreground mb-2 sm:mb-4">
          Three-Phase Implementation Framework
        </h2>
        <div className="space-y-8">
          {phases.map((phase) => (
            <PhaseCard key={phase.number} phase={phase} />
          ))}
        </div>
      </section>

      <section>
        <div className="text-center mb-2 sm:mb-4">
          <h2 className="font-headline text-2xl sm:text-3xl font-semibold text-foreground mb-2 sm:mb-4">
            <span className="text-accent">Quantified</span> Impact Projections
          </h2>
          <p className="font-body text-base sm:text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Measurable outcomes tracked quarterly with third-party verification, demonstrating systematic progress toward 20-year transformation goals.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6">
          {impactMetrics.map((metric, index) => (
            <ImpactMetricCard key={index} metric={metric} />
          ))}
        </div>
      </section>

      <section>
        <div className="text-center mb-2 sm:mb-4">
          <h2 className="font-headline text-2xl sm:text-3xl font-semibold text-foreground mb-2 sm:mb-4">
            <span className="text-accent">Strategic</span> Sector Focus
          </h2>
          <p className="font-body text-base sm:text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Disciplined capital allocation across four priority sectors, each selected for job creation potential, scalability, and sustainable returns.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
          {sectorFocuses.map((sector, index) => (
            <SectorFocusCard key={index} sector={sector} />
          ))}
        </div>
      </section>

      <section>
        <TimelineVisualization events={timelineEvents} />
      </section>

      <section>
        <div className="text-center mb-2 sm:mb-4">
          <h2 className="font-headline text-3xl font-semibold text-foreground mb-2 sm:mb-4">
            Detailed Documentation
          </h2>
          <p className="font-body text-base sm:text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Comprehensive frameworks, methodologies, and progress reports for institutional investors and strategic partners.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {downloadResources.map((resource, index) => (
            <DownloadResourceCard key={index} resource={resource} />
          ))}
        </div>
      </section>

      <section>
        <NewsletterSubscription />
      </section>
    </div>
  );
}